// @ts-ignore
import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { FormsModule } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { ImpressaoCompraSelecao, MODELOS_COMPRA } from '../compra-impressao/compra-impressao.model';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { EmpresaStateService, EmpresaOpcao } from '../../services/empresa-state.service';
import { ProdutoService } from '../../services/produto.service';

interface PreCompraLinha {
  nunota:       number;
  codemp:       number;
  dtmov:        string;
  fornecedor:   string;
  docparc:      string;
  empresa:      string;
  vlrnota:      number;
  vlrmoeda:     number;
  vlritens:     number;
  vlritensMoe:  number;
  vlripi:       number;
  vlrst:        number;
  vlrcoreusd:   number;
  status:       string;
  comprador:    string;
  tipoOperacao: string;
  codTipOper:   number;
  observacao:   string;
  gplStatus:    string;
  prioridade:   string;
  numPo:        string;
  numOrdem:     string;
  numCotacao:   string;
  tipoGpl:      string;
  /** PCP_ID da pré-compra no Aerosys (TGFCAB.GPL_PCRIDAEROSYS). */
  pcpIdAerosys: number | null;
  /** Nº(s) dos pedidos de compra (Sankhya) vinculados, separados por vírgula (TGFCAB.GPL_IDSPEDCOMPRA). */
  idsPedCompra: string;
}

// @ts-ignore
@Component({
  selector: 'app-pre-compra-lista',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pre-compra-lista.component.html',
  styleUrls: ['./pre-compra-lista.component.css']
})
export class PreCompraListaComponent implements OnInit, OnDestroy {

  @Output() novoPedido   = new EventEmitter<void>();
  @Output() editarPedido = new EventEmitter<number>();
  @Output() imprimirPedido = new EventEmitter<ImpressaoCompraSelecao>();

  readonly relatoriosImpressao = MODELOS_COMPRA.filter(relatorio => relatorio.modelo !== 'PEDIDO_COMPRA');

  // ── Filtros ───────────────────────────────────────────────
  dtDe              = '';
  dtAte             = '';
  mostrarCancelados = false;
  termoBusca        = '';
  termoBuscaPn      = '';
  private _pnTimer: any = null;

  // ── Lista ─────────────────────────────────────────────────
  carregando = false;
  pedidos: PreCompraLinha[] = [];
  erro = '';

  // ── Paginação ─────────────────────────────────────────────
  paginaAtual    = 1;
  itensPorPagina = 11;
  readonly opcoesPorPagina = [11, 25, 50, 100];

  // ── Dropdown ações ────────────────────────────────────────
  dropdownAberto: number | null = null;

  private empresaSub?: Subscription;

  mensagemCentral: string | null = null;
  private _mensagemCentralTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Clique no PN copia o valor e mostra mensagem central por 2s. */
  copiarPn(pn: string | null | undefined, ev?: Event): void {
    ev?.stopPropagation();
    const v = String(pn ?? '').trim();
    if (!v) return;
    let ok = true;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(v).catch(() => {});
      } else {
        const ta = document.createElement('textarea');
        ta.value = v;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch { ok = false; }
    this.mensagemCentral = ok ? `PN copiado com sucesso: ${v}` : 'Não foi possível copiar o PN.';
    if (this._mensagemCentralTimeout) clearTimeout(this._mensagemCentralTimeout);
    this._mensagemCentralTimeout = setTimeout(() => { this.mensagemCentral = null; }, 2000);
  }

  constructor(
    private readonly gateway: SankhyaGatewayService,
    readonly empresa: EmpresaStateService,
    private readonly produtoService: ProdutoService
  ) {}

  private numBr(v: unknown): number {
    let s = String(v ?? '').trim();
    if (!s) return 0;
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  }

  /**
   * Ao AUTORIZAR a Pré-Compra: grava o preço dos itens como Preço do Fabricante da peça (U$ na importação,
   * R$ no nacional), igual ao salvar da Edição/Cotação (recalcula Total Custo e Preço de Venda com as margens
   * atuais do produto; Sankhya + Aerosys). Best-effort: devolve avisos, nunca bloqueia a autorização.
   */
  private async atualizarPrecoFabricanteDosItens(p: PreCompraLinha): Promise<string[]> {
    const avisos: string[] = [];
    try {
      const nacional = (p.tipoGpl || '').toUpperCase().startsWith('NAC');
      const res: any = await firstValueFrom(this.gateway.buscarDetalhesPedidoVenda(p.nunota));
      const body = res?.responseBody ?? res;
      const d = body?.sucesso ? (body.detalhe ?? body) : null;
      const brutos: any[] = d?.itens ?? [];
      const porProduto = new Map<number, any>();
      for (const it of brutos) {
        const st = String(it.statusItem || '').toUpperCase();
        const cod = Number(it.codprod) || 0;
        if (cod > 0 && st !== 'CANCELADO') porProduto.set(cod, it);
      }
      for (const [cod, it] of porProduto) {
        try {
          // Moeda pelo FABRICANTE do item (TGFMAR.AD_ORIGEM); sem a origem, segue o tipo da Pré-Compra.
          let nacionalItem = nacional;
          try {
            const pe: any = await firstValueFrom(this.gateway.buscarPrecoExc(cod, Number(p.codemp) || Number(this.empresa.codEmpReal) || 0) as any);
            const inf = (pe?.responseBody ?? pe);
            const o = (inf?.info ?? inf)?.origemNacional;
            if (typeof o === 'boolean') nacionalItem = o;
          } catch { /* mantém o tipo da Pré-Compra */ }
          let preco = nacionalItem
            ? (Number(it.vlrunit) || 0)
            : (Number(it.precoUsd) || Number(it.vlrunitmoe) || 0);
          // O preço do item leva o Core embutido quando ativo: o Preço do Fabricante é sem Core.
          if (!nacionalItem &&String(it.controlaCore ?? 'N').toUpperCase() === 'S') {
            preco -= Number(it.vlrCore) || 0;
          }
          preco = Math.round(preco * 100) / 100;
          if (!(preco > 0)) continue;

          const selRes: any = await firstValueFrom(this.gateway.produtoSelecionadoEdicao({ CODPROD: cod }) as any);
          const prod = (selRes?.responseBody ?? selRes)?.produto;
          if (!prod) { avisos.push(`produto ${cod} não encontrado para atualizar o preço`); continue; }
          const atual = this.numBr(prod.vlrComerc);
          if (Math.abs(atual - preco) < 0.005) continue;   // preço igual: nada a atualizar

          let complementos: Record<string, string> = {};
          try {
            const comp: any = await firstValueFrom(this.produtoService.buscarProdutoCompleto(cod));
            complementos = comp?.dados ?? {};
          } catch { /* segue sem os complementares */ }

          const custoPct = this.numBr(prod.margCusto);
          const lucroPct = this.numBr(prod.margLucro);
          const core = this.numBr(prod.core);
          const totalCusto = Math.round(preco * (1 + custoPct / 100) * 100) / 100;
          const precoVenda = Math.round(totalCusto * (1 + lucroPct / 100) * 100) / 100;
          const variacao = atual > 0 ? Math.round(((preco - atual) / atual) * 10000) / 100 : 0;
          const label = String(complementos['GPL_LABEL'] || complementos['DESCRESTRAG'] || '').trim();

          const payload: Record<string, unknown> = {
            CODPROD: cod,
            FORNEC: p.fornecedor || '',
            PRECO: preco,
            VARIACAO: variacao,
            CORE: core,
            LUCRO: lucroPct,
            CUSTO: custoPct,
            PRECOVENDA: precoVenda,
            TOTALCUSTO: totalCusto,
            REFFORN: prod.partNumber ?? '',
            DESCRPROD: prod.descricao ?? '',
            CODVOL: prod.codVol ?? '',
            TEMSERIE: prod.temSerie || 'N',
            TEMCORE: prod.temCore === 'S' ? 'S' : 'N',
            MARCA: prod.marca ?? '',
            CONDICAO: prod.condicao ?? '',
            OBSTECNICA: prod.obsTecnica ?? '',
            OBSINTERNA: prod.obsInterna ?? '',
            GPL_LABEL: label,
            CODLOCALPADRAO: prod.codLocalPadrao ?? null,
            CODEMP_AEROSYS: Number(this.empresa.codEmpReal) || undefined
          };
          const sv: any = await firstValueFrom(this.gateway.salvarCotacaoProdutoEdicao(payload) as any);
          const svBody = sv?.responseBody ?? sv;
          if (!svBody?.sucesso) {
            avisos.push(`produto ${cod}: ${svBody?.mensagem || 'não foi possível atualizar o preço no Sankhya'}`);
            continue;
          }
          try {
            await firstValueFrom(this.produtoService.sincronizarEdicaoProdutoAerosys({
              ...payload,
              ATUALIZAR_DATA_PRECO: true,
              MOEDA_SIMBOLO: nacionalItem ? 'R$' : 'U$',
              CODMARCA: String(complementos['CODMARCA'] ?? '').trim()
            }));
          } catch {
            avisos.push(`produto ${cod}: preço atualizado no Sankhya, mas não no Aerosys`);
          }
        } catch (e: any) {
          avisos.push(`produto ${cod}: ${e?.error?.erro ?? e?.message ?? 'falha ao atualizar o preço'}`);
        }
      }
    } catch (e: any) {
      avisos.push('não foi possível atualizar o Preço do Fabricante das peças: ' + (e?.message ?? ''));
    }
    return avisos;
  }

  ngOnInit(): void {
    const hoje = new Date();
    this.dtAte = this.toInputDate(hoje);
    const trintaDiasAtras = new Date(hoje);
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    this.dtDe = this.toInputDate(trintaDiasAtras);

    this.empresaSub = this.empresa.empresaMudou.subscribe(() => this.buscar());
    this.buscar();
  }

  ngOnDestroy(): void {
    this.empresaSub?.unsubscribe();
    clearTimeout(this._pnTimer);
  }

  buscar(): void {
    // Empresa virtual (ex.: 12): CODEMP real nas notas + filtro por GPL_EMPRESA_VIRTUAL.
    const empVirtual = this.empresa.selecionadaEhVirtual ? Number(this.empresa.selecionada) : 0;
    const codEmp = empVirtual > 0 ? Number(this.empresa.codEmpReal) : Number(this.empresa.selecionada);
    if (!codEmp || !this.dtDe || !this.dtAte) return;

    this.carregando = true;
    this.erro = '';
    this.paginaAtual = 1;

    const dtDe = this.toSankhyaDate(this.dtDe);
    const dtAte = this.toSankhyaDate(this.dtAte);

    this.gateway.listarPreCompras(codEmp, dtDe, dtAte, this.mostrarCancelados, this.termoBuscaPn, empVirtual).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        const lista = body?.lista ?? [];
        this.pedidos = lista.map((r: any): PreCompraLinha => ({
          nunota:       Number(r.nunota      ?? 0),
          codemp:       Number(r.codemp      ?? 0),
          dtmov:        r.dtmov              ?? '',
          fornecedor:   r.fornecedor         ?? '',
          docparc:      r.docparc            ?? '',
          empresa:      r.empresa            ?? '',
          vlrnota:      Number(r.vlrnota     ?? 0),
          vlrmoeda:     Number(r.vlrmoeda    ?? 1),
          vlritens:     Number(r.vlritens    ?? 0),
          vlritensMoe:  Number(r.vlritensMoe ?? 0),
          vlripi:       Number(r.vlripi      ?? 0),
          vlrst:        Number(r.vlrst       ?? 0),
          vlrcoreusd:   Number(r.vlrcoreusd  ?? 0),
          status:       r.status             ?? '',
          comprador:    r.comprador          ?? '',
          tipoOperacao: r.tipoOperacao       ?? '',
          codTipOper:   Number(r.codTipOper  ?? 0),
          observacao:   r.observacao         ?? '',
          gplStatus:    r.gplStatus          ?? '',
          prioridade:   r.prioridade         ?? '',
          numPo:        String(r.numPo       ?? '').trim(),
          numOrdem:     String(r.numOrdem    ?? '').trim(),
          numCotacao:   String(r.numCotacao  ?? '').trim(),
          tipoGpl:      r.tipoGpl            ?? '',
          pcpIdAerosys: r.pcrIdAerosys != null && Number(r.pcrIdAerosys) > 0 ? Number(r.pcrIdAerosys) : null,
          idsPedCompra: String(r.idsPedCompra ?? '').trim(),
        }));
        this.carregando = false;
      },
      error: (err: any) => {
        this.erro = err?.error?.erro ?? err?.message ?? 'Erro ao buscar pré-compras.';
        this.carregando = false;
      }
    });
  }

  // ── Filtros reativos ──────────────────────────────────────

  // ── Ordenação por coluna (clique no cabeçalho) ──
  ordenarCampo: string | null = null;
  ordenarAsc = true;

  ordenar(campo: string): void {
    if (this.ordenarCampo === campo) {
      this.ordenarAsc = !this.ordenarAsc;
    } else {
      this.ordenarCampo = campo;
      this.ordenarAsc = true;
    }
    this.paginaAtual = 1;
  }

  private valorOrdenacao(p: PreCompraLinha, campo: string): number | string {
    switch (campo) {
      case 'nunota':  return Number(p.nunota) || 0;
      case 'codemp':  return Number(p.codemp) || 0;
      case 'status':  return (p.gplStatus || '').toLowerCase();
      case 'tipo':    return this.fmtTipo(p.tipoGpl).toLowerCase();
      case 'dtmov': {
        const m = String(p.dtmov || '').match(/(\d{2})\/(\d{2})\/(\d{4})(?:\D+(\d{2}):(\d{2}))?/);
        return m ? new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0)).getTime() : 0;
      }
      case 'vlr':     return this.vlrTotal(p) || 0;
      case 'vlrusd':  return this.vlrTotalUsd(p) || 0;
      case 'pcpIdAerosys': return Number(p.pcpIdAerosys) || 0;
      default:        return String((p as any)[campo] ?? '').toLowerCase();
    }
  }

  get pedidosFiltrados(): PreCompraLinha[] {
    const lista = this.pedidosFiltradosSemOrdem;
    const campo = this.ordenarCampo;
    if (!campo) return lista;
    const dir = this.ordenarAsc ? 1 : -1;
    return [...lista].sort((a, b) => {
      const va = this.valorOrdenacao(a, campo);
      const vb = this.valorOrdenacao(b, campo);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true }) * dir;
    });
  }

  private get pedidosFiltradosSemOrdem(): PreCompraLinha[] {
    const termo = this.termoBusca.trim().toLowerCase();
    if (!termo) return this.pedidos;
    return this.pedidos.filter(p =>
      String(p.nunota ?? '').includes(termo) ||
      (p.fornecedor  || '').toLowerCase().includes(termo) ||
      (p.docparc    || '').toLowerCase().includes(termo) ||
      (p.numPo      || '').toLowerCase().includes(termo) ||
      (p.numOrdem   || '').toLowerCase().includes(termo) ||
      (p.numCotacao || '').toLowerCase().includes(termo) ||
      (p.comprador   || '').toLowerCase().includes(termo)
    );
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.pedidosFiltrados.length / this.itensPorPagina));
  }

  get pedidosPagina(): PreCompraLinha[] {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    return this.pedidosFiltrados.slice(inicio, inicio + this.itensPorPagina);
  }

  get paginas(): number[] {
    const total = this.totalPaginas;
    const atual = this.paginaAtual;
    const range: number[] = [];
    for (let i = Math.max(1, atual - 2); i <= Math.min(total, atual + 2); i++) {
      range.push(i);
    }
    return range;
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas) return;
    this.paginaAtual = p;
  }

  onItensPorPaginaChange(): void { this.paginaAtual = 1; }
  onTermoBuscaChange(): void     { this.paginaAtual = 1; }
  onMostrarCanceladosChange(): void { this.buscar(); }

  onTermoBuscaPnChange(): void {
    clearTimeout(this._pnTimer);
    this._pnTimer = setTimeout(() => this.buscar(), 500);
  }

  // ── Cálculos de valor ─────────────────────────────────────

  /** Soma dos itens (preco de custo, sem IPI/ICMS-ST) -- igual ao total da tela do pedido. */
  vlrTotal(p: PreCompraLinha): number {
    return p.vlritens > 0 ? p.vlritens : (p.vlrnota || 0);
  }

  vlrTotalUsd(p: PreCompraLinha): number {
    if (p.vlritensMoe > 0) return p.vlritensMoe;
    const cotacao = p.vlrmoeda || 1;
    return this.vlrTotal(p) / cotacao;
  }

  fmtBrl(v: number | null | undefined): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  }

  fmtUsd(v: number | null | undefined): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(v);
  }

  /** GPL_TIPO (TGFCAB) -- código gravado sem acento (IMPORTACAO), exibido
   *  com o rótulo correto (IMPORTAÇÃO). */
  fmtTipo(v: string | null | undefined): string {
    if (!v) return '—';
    if (v === 'IMPORTACAO') return 'IMPORTAÇÃO';
    return v;
  }

  /** Cor do badge de Tipo (Origem) -- IMPORTACAO em lilás, NACIONAL em azul-petróleo,
   *  igual ao padrão visual do sistema legado (Aerosys). */
  tipoClass(v: string | null | undefined): string {
    const t = (v || '').toUpperCase().trim();
    if (t === 'IMPORTACAO' || t === 'IMPORTAÇÃO') return 'tipo-importacao';
    if (t === 'NACIONAL') return 'tipo-nacional';
    return 'tipo-outro';
  }

  /** Cor do badge de Status (GPL_STATUS) -- mesmo estilo visual (badge colorido)
   *  do sistema legado (Aerosys), mas usando os valores do Sankhya. COMPRADO e
   *  PARCIAL são os dois valores que faltavam no domínio GPL_STATUS (o usuário
   *  vai cadastrar no DataDictionary) -- já mapeados aqui com as mesmas cores
   *  do legado (verde/laranja) assim que existirem. */
  statusLegadoClass(status: string | null | undefined): string {
    const s = (status || '').toUpperCase().trim();
    if (s === 'CANCELADO') return 'st-leg-cancelado';
    if (s === 'COMPRADO') return 'st-leg-comprado';
    if (s === 'AUTORIZADO') return 'st-leg-autorizado';
    if (s === 'PARCIAL' || s === 'BACKORDERS') return 'st-leg-parcial';
    return 'st-leg-outro';
  }

  statusClass(status: string | null | undefined): string {
    const s = (status || '').toUpperCase().trim();
    switch (s) {
      case 'COMPRADO':
      case 'ENTREGUE':
      case 'FATURADO':
      case 'FINALIZADO':
      case 'PRONTO':
        return 'status-liberado';   // verde
      case 'AUTORIZADO':
      case 'ESTOQUE':
        return 'status-atendido';   // azul
      case 'ABERTO':
      case 'PENDENTE':
      case 'PARCIAL':
        return 'status-pendente';   // amarelo
      case 'CANCELADO':
      case 'ESTORNAR':
        return 'status-cancelado';  // vermelho
      case 'CONFERENCIA':
      case 'LOGISTICA':
      case 'CONTAGEM':
      case 'SEPARACAO':
      case 'SEPARACAO TOTAL':
        return 'status-processo';   // roxo
      case 'GARANTIA':
        return 'status-alerta';     // laranja
      case 'BAIXADO':
        return 'status-outro';      // cinza
      default:
        return 'status-outro';
    }
  }

  /** Código da empresa no mesmo formato do legado (0000012). */
  fmtCodEmp(codemp: number | null | undefined): string {
    if (codemp == null) return '—';
    return String(codemp);   // código da empresa no Sankhya, sem zeros à esquerda (igual ao Pedido de Compra)
  }

  // ── Dropdown ─────────────────────────────────────────────

  toggleDropdown(event: Event, p: PreCompraLinha): void {
    event.stopPropagation();
    this.dropdownAberto = this.dropdownAberto === p.nunota ? null : p.nunota;
  }

  /** Duplo clique na linha abre o pedido em edição -- mesmo padrão do
   *  Pedido de Venda (onDblClickLinha em pedido-venda-lista.component.ts).
   *  Não checa status aqui: a própria tela de edição decide se abre travada
   *  (só leitura) ou editável, conforme o GPL_STATUS do pedido. */
  onDblClickLinha(p: PreCompraLinha): void {
    this.editarPedido.emit(p.nunota);
  }

  fecharDropdown(): void { this.dropdownAberto = null; }

  // ── Diálogo padrão da tela (substitui alert()/confirm() nativos do navegador) ──
  dialogo: {
    tipo: 'confirmar' | 'sucesso' | 'erro' | 'aviso';
    titulo: string;
    mensagem: string;
    detalhes: string[];
    textoConfirmar: string;
    perigo: boolean;
  } | null = null;
  private dialogoResolve: ((ok: boolean) => void) | null = null;

  /** Pergunta ao usuário num modal; resolve true ao confirmar e false ao cancelar. */
  confirmar(mensagem: string, opcoes: { titulo?: string; textoConfirmar?: string; perigo?: boolean } = {}): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.dialogoResolve = resolve;
      this.dialogo = {
        tipo: 'confirmar',
        titulo: opcoes.titulo ?? 'Confirmação',
        mensagem,
        detalhes: [],
        textoConfirmar: opcoes.textoConfirmar ?? 'Confirmar',
        perigo: !!opcoes.perigo
      };
    });
  }

  /** Mostra o resultado de uma operação num modal (OK fecha). */
  informar(mensagem: string, tipo: 'sucesso' | 'erro' | 'aviso', detalhes: string[] = [], titulo?: string): Promise<void> {
    return new Promise<void>(resolve => {
      this.dialogoResolve = () => resolve();
      this.dialogo = {
        tipo,
        titulo: titulo ?? (tipo === 'sucesso' ? 'Concluído' : tipo === 'erro' ? 'Não foi possível concluir' : 'Atenção'),
        mensagem,
        detalhes,
        textoConfirmar: 'OK',
        perigo: false
      };
    });
  }

  fecharDialogo(ok: boolean): void {
    const resolve = this.dialogoResolve;
    this.dialogoResolve = null;
    this.dialogo = null;
    resolve?.(ok);
  }
  pedidoSelecionado: number | null = null;

  selecionarLinha(event: Event, p: PreCompraLinha): void {
    event.stopPropagation();
    this.dropdownAberto = null;
    this.pedidoSelecionado = this.pedidoSelecionado === p.nunota ? null : p.nunota;
  }


  /** Item selecionado na grade, só quando a grade aberta é desta pré-compra. */
  private itemSelecionadoDe(p: PreCompraLinha): any | null {
    if (!this.itensAberto || this.itensNunota !== p.nunota || this.itemSelecionado == null) return null;
    return this.itens[this.itemSelecionado] ?? null;
  }
  /** Itens marcados (checkbox / clique na linha) da grade aberta desta pré-compra. */
  private itensMarcadosDe(p: PreCompraLinha): any[] {
    if (!this.itensAberto || this.itensNunota !== p.nunota) return [];
    return Array.from(this.itensMarcados).sort((a, b) => a - b).map(i => this.itens[i]).filter(Boolean);
  }
  /** "Cancelar item selecionado" vale para pré-compras ABERTO, AUTORIZADO e BACKORDERS (os demais cancelamentos seguem as regras próprias). */
  private statusPermiteCancelarSelecionados(p: PreCompraLinha): boolean {
    const st = this.statusNormalizado(p);
    return st === 'ABERTO' || st === 'AUTORIZADO' || st === 'BACKORDERS';
  }
  private itensCancelaveisMarcados(p: PreCompraLinha): any[] {
    return this.itensMarcadosDe(p).filter(i => i.statusItem === 'ABERTO');
  }
  podeCancelarItemSelecionado(p: PreCompraLinha): boolean {
    return this.statusPermiteCancelarSelecionados(p) && this.itensCancelaveisMarcados(p).length > 0;
  }
  rotuloCancelarSelecionados(p: PreCompraLinha): string {
    const n = this.itensMarcadosDe(p).length;
    return n > 1 ? `Cancelar itens selecionados (${n})` : 'Cancelar item selecionado';
  }

  podeReabrirItens(p: PreCompraLinha): boolean {
    const st = this.statusNormalizado(p);
    return st === 'BACKORDERS' || st === 'COMPRADO';
  }
  /** "Reabrir item selecionado" vale para pré-compras ABERTO, AUTORIZADO, BACKORDERS e COMPRADO. */
  private statusPermiteReabrirSelecionados(p: PreCompraLinha): boolean {
    const st = this.statusNormalizado(p);
    return st === 'ABERTO' || st === 'AUTORIZADO' || st === 'BACKORDERS' || st === 'COMPRADO';
  }
  private itensReabriveisMarcados(p: PreCompraLinha): any[] {
    return this.itensMarcadosDe(p).filter(i => i.statusItem === 'CANCELADO');
  }
  podeReabrirItemSelecionado(p: PreCompraLinha): boolean {
    return this.statusPermiteReabrirSelecionados(p) && this.itensReabriveisMarcados(p).length > 0;
  }
  rotuloReabrirSelecionados(p: PreCompraLinha): string {
    const n = this.itensMarcadosDe(p).length;
    return n > 1 ? `Reabrir itens selecionados (${n})` : 'Reabrir item selecionado';
  }
  tituloCancelarItemSelecionado(p: PreCompraLinha): string {
    if (!this.statusPermiteCancelarSelecionados(p)) return 'Disponível apenas para pré-compras ABERTO, AUTORIZADO ou BACKORDERS';
    if (this.itensMarcadosDe(p).length === 0) return 'Abra os itens da pré-compra e selecione um ou mais itens (clique na linha ou use a caixa de seleção)';
    return this.podeCancelarItemSelecionado(p) ? '' : 'Só itens ABERTOS podem ser cancelados';
  }
  tituloReabrirItens(p: PreCompraLinha): string {
    return this.podeReabrirItens(p) ? '' : 'Disponível apenas para pedidos BACKORDERS ou COMPRADO';
  }
  tituloReabrirItemSelecionado(p: PreCompraLinha): string {
    if (!this.statusPermiteReabrirSelecionados(p)) return 'Disponível apenas para pré-compras ABERTO, AUTORIZADO, BACKORDERS ou COMPRADO';
    if (this.itensMarcadosDe(p).length === 0) return 'Abra os itens da pré-compra e selecione um ou mais itens (clique na linha ou use a caixa de seleção)';
    return this.podeReabrirItemSelecionado(p) ? '' : 'Só itens CANCELADOS podem ser reabertos';
  }

  /** Executa a ação de itens, mostra o resultado e recarrega a lista (e a grade de itens, se aberta). */
  private async executarAcaoItens(p: PreCompraLinha, chamada: () => Promise<any>, verbo: string, campo: 'cancelados' | 'reabertos'): Promise<void> {
    try {
      const res: any = await chamada();
      if (res?.sucesso) {
        const avisos: string[] = [];
        if (res?.aerosysSyncOk === false) avisos.push('não foi possível atualizar os itens no Aerosys');
        if (res?.aerosysStatusOk === false) avisos.push('não foi possível atualizar o status do cabeçalho no Aerosys');
        if (res?.statusCabecalhoErro) avisos.push(res.statusCabecalhoErro);
        const cab = res?.statusCabecalho ? ` Cabeçalho: ${res.statusCabecalho}.` : '';
        this.buscar();
        await this.informar(`${res[campo] ?? 0} item(ns) ${verbo}.${cab}`, avisos.length ? 'aviso' : 'sucesso', avisos.map(a => a.charAt(0).toUpperCase() + a.slice(1) + '.'));
        if (this.itensAberto && this.itensNunota === p.nunota) {
          this.itensMarcados.clear();
          this.itemSelecionado = null;
          await this.abrirItens(new Event('click'), p, true);
        }
      } else {
        await this.informar(res?.erro ?? 'Erro desconhecido.', 'erro');
      }
    } catch (e: any) {
      await this.informar(e?.error?.erro ?? e?.message ?? 'Erro desconhecido.', 'erro');
    }
  }

  /** Ações > Cancelar itens em aberto: pergunta, cancela no Sankhya e no Aerosys e recarrega a lista. */
  async cancelarItensAbertos(p: PreCompraLinha): Promise<void> {
    this.dropdownAberto = null;
    if (!this.podeCancelarItensEmAberto(p)) return;
    if (!(await this.confirmar(`Deseja realmente cancelar TODOS os itens em aberto do Pré-Compra #${p.nunota}?`, { titulo: 'Cancelar itens em aberto', textoConfirmar: 'Cancelar itens', perigo: true }))) return;
    await this.executarAcaoItens(p, () => firstValueFrom(this.gateway.cancelarItensAbertosPreCompra(p.nunota)), 'cancelado(s)', 'cancelados');
  }

  /** Ações > Cancelar item(ns) selecionado(s): só os itens ABERTOS marcados. */
  async cancelarItemSelecionado(p: PreCompraLinha): Promise<void> {
    this.dropdownAberto = null;
    if (!this.podeCancelarItemSelecionado(p)) return;
    const marcados = this.itensMarcadosDe(p);
    const alvo = this.itensCancelaveisMarcados(p);
    const seqs = alvo.map(i => Number(i.sequencia) || 0).filter(n => n > 0);
    if (seqs.length === 0) { await this.informar('Item sem sequência no pedido.', 'aviso'); return; }
    const ignorados = marcados.length - alvo.length;
    const quais = alvo.length === 1 ? `o item selecionado (${alvo[0].pn || alvo[0].codprodInterno || 'seq ' + seqs[0]})` : `os ${alvo.length} itens selecionados`;
    const aviso = ignorados > 0 ? ` (${ignorados} marcado(s) que não estão ABERTOS serão ignorados)` : '';
    if (!(await this.confirmar(`Deseja realmente cancelar ${quais} do Pré-Compra #${p.nunota}?${aviso}`, { titulo: alvo.length === 1 ? 'Cancelar item' : 'Cancelar itens', textoConfirmar: alvo.length === 1 ? 'Cancelar item' : 'Cancelar itens', perigo: true }))) return;
    await this.executarAcaoItens(p, () => firstValueFrom(this.gateway.cancelarItensSelecionadosPreCompra(p.nunota, seqs)), 'cancelado(s)', 'cancelados');
  }

  /** Ações > Reabrir todos os itens cancelados. */
  async reabrirTodosItens(p: PreCompraLinha): Promise<void> {
    this.dropdownAberto = null;
    if (!this.podeReabrirItens(p)) return;
    if (!(await this.confirmar(`Deseja realmente reabrir TODOS os itens cancelados do Pré-Compra #${p.nunota}?`, { titulo: 'Reabrir itens', textoConfirmar: 'Reabrir itens' }))) return;
    await this.executarAcaoItens(p, () => firstValueFrom(this.gateway.reabrirItensPreCompra(p.nunota, [])), 'reaberto(s)', 'reabertos');
  }

  /** Ações > Reabrir item(ns) selecionado(s): só os itens CANCELADOS marcados. */
  async reabrirItemSelecionado(p: PreCompraLinha): Promise<void> {
    this.dropdownAberto = null;
    if (!this.podeReabrirItemSelecionado(p)) return;
    const marcados = this.itensMarcadosDe(p);
    const alvo = this.itensReabriveisMarcados(p);
    const seqs = alvo.map(i => Number(i.sequencia) || 0).filter(n => n > 0);
    if (seqs.length === 0) { await this.informar('Item sem sequência no pedido.', 'aviso'); return; }
    const ignorados = marcados.length - alvo.length;
    const quais = alvo.length === 1 ? `o item selecionado (${alvo[0].pn || alvo[0].codprodInterno || 'seq ' + seqs[0]})` : `os ${alvo.length} itens selecionados`;
    const aviso = ignorados > 0 ? ` (${ignorados} marcado(s) que não estão CANCELADOS serão ignorados)` : '';
    if (!(await this.confirmar(`Deseja realmente reabrir ${quais} do Pré-Compra #${p.nunota}?${aviso}`, { titulo: alvo.length === 1 ? 'Reabrir item' : 'Reabrir itens', textoConfirmar: alvo.length === 1 ? 'Reabrir item' : 'Reabrir itens' }))) return;
    await this.executarAcaoItens(p, () => firstValueFrom(this.gateway.reabrirItensPreCompra(p.nunota, seqs)), 'reaberto(s)', 'reabertos');
  }

  // ── Ações > Previsão de entrega ──
  previsaoModalAberto = false;
  previsaoEscopo: 'todos' | 'selecionado' = 'todos';
  previsaoPre: PreCompraLinha | null = null;
  previsaoData = '';          // yyyy-MM-dd (input type="date")
  previsaoSalvando = false;
  previsaoErro = '';

  podePrevisaoSelecionado(p: PreCompraLinha): boolean { return this.itensMarcadosDe(p).length > 0; }
  tituloPrevisaoSelecionado(p: PreCompraLinha): string {
    return this.podePrevisaoSelecionado(p) ? '' : 'Abra os itens da pré-compra e selecione um ou mais itens (clique na linha ou use a caixa de seleção)';
  }

  abrirPrevisaoEntrega(p: PreCompraLinha, escopo: 'todos' | 'selecionado'): void {
    this.dropdownAberto = null;
    if (escopo === 'selecionado' && !this.podePrevisaoSelecionado(p)) return;
    this.previsaoPre = p;
    this.previsaoEscopo = escopo;
    this.previsaoErro = '';
    const marc = escopo === 'selecionado' ? this.itensMarcadosDe(p) : [];
    const atual = marc.length === 1 ? String(marc[0]?.previsaoEntrega || '') : '';
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(atual);
    this.previsaoData = m ? `${m[3]}-${m[2]}-${m[1]}` : '';
    this.previsaoModalAberto = true;
  }

  fecharPrevisaoEntrega(): void { this.previsaoModalAberto = false; }

  async confirmarPrevisaoEntrega(): Promise<void> {
    const p = this.previsaoPre;
    if (this.previsaoSalvando || !p) return;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.previsaoData);
    if (!m) { this.previsaoErro = 'Informe a data da previsão de entrega.'; return; }
    const dataBr = `${m[3]}/${m[2]}/${m[1]}`;
    const seqs = this.previsaoEscopo === 'selecionado'
      ? this.itensMarcadosDe(p).map((i: any) => Number(i.sequencia) || 0).filter((n: number) => n > 0)
      : [];
    if (this.previsaoEscopo === 'selecionado' && seqs.length === 0) { this.previsaoErro = 'Itens sem sequência na pré-compra.'; return; }
    this.previsaoSalvando = true;
    this.previsaoErro = '';
    try {
      const res: any = await firstValueFrom(this.gateway.atualizarPrevisaoEntregaItens(p.nunota, dataBr, seqs));
      const body = res?.responseBody ?? res;
      if (body?.sucesso !== true) {
        console.warn('[Previsão de entrega] resposta do serviço:', res);
        const detalhe = body?.erro || res?.statusMessage || body?.statusMessage || (res ? JSON.stringify(res).slice(0, 300) : '');
        this.previsaoErro = 'O serviço não confirmou a gravação' + (detalhe ? ': ' + detalhe : '.');
        return;
      }
      if (Number(body?.atualizados) === 0) {
        this.previsaoErro = 'Nenhum item foi atualizado no banco (sequência/pré-compra não encontrados).';
        return;
      }
      this.previsaoModalAberto = false;
      // Recarrega os itens abertos para mostrar a previsão gravada.
      if (this.itensAberto && this.itensNunota === p.nunota) {
        await this.abrirItens(new Event('refresh'), p, true);
      }
    } catch (err: any) {
      this.previsaoErro = err?.message ?? 'Não foi possível gravar a previsão de entrega.';
    } finally {
      this.previsaoSalvando = false;
    }
  }

  // ── Ações > Gerar Pré-Venda ──
  gvAberto = false;
  gvPre: PreCompraLinha | null = null;
  gvCarregando = false;
  gvGerando = false;
  gvErro = '';
  gvCodParc = 0;
  gvNomeCliente = '';
  gvCodEmp = 0;
  gvEmpresaVirtual = 0;
  gvStep: 1 | 2 = 1;
  gvEmpresaSel = '';   // empresa (codEmp do menu, real ou virtual) em que a Pré-Venda será gerada
  get gvEmpresasOpcoes(): EmpresaOpcao[] { return this.empresa.opcoes.filter(e => e.codEmp !== '100'); }
  gvItens: { marcado: boolean; codprod: number; pn: string; descricao: string; unidade: string; sequencia: number; qtdMax: number; qtd: number }[] = [];

  get gvMarcados(): number { return this.gvItens.filter(i => i.marcado).length; }
  get gvTodosMarcados(): boolean { return this.gvItens.length > 0 && this.gvItens.every(i => i.marcado); }
  gvToggleTodos(): void { const v = !this.gvTodosMarcados; this.gvItens.forEach(i => i.marcado = v); }

  fecharGerarPreVenda(): void { if (!this.gvGerando) this.gvAberto = false; }

  async abrirGerarPreVenda(p: PreCompraLinha): Promise<void> {
    this.dropdownAberto = null;
    this.gvPre = p;
    this.gvAberto = true;
    this.gvStep = 1;
    this.gvCarregando = true;
    this.gvErro = '';
    this.gvItens = [];
    this.gvCodParc = 0;
    this.gvNomeCliente = '';
    try {
      const res: any = await firstValueFrom(this.gateway.buscarDetalhesPedidoVenda(p.nunota));
      const body = res?.responseBody ?? res;
      const d = body?.sucesso ? (body.detalhe ?? body) : null;
      if (!d) { this.gvErro = 'Pré-Compra não encontrada.'; return; }
      // Empresa padrão = a da própria Pré-Compra (a virtual/fictícia, quando houver); o usuário pode trocar.
      this.gvEmpresaSel = String(Number(d.gplEmpresaVirtual) > 0 ? Number(d.gplEmpresaVirtual) : (Number(d.codemp) || Number(p.codemp) || ''));
      await this.gvAplicarEmpresa();
      const brutos: any[] = d.itens ?? [];
      this.gvItens = brutos
        .filter((it: any) => String(it.statusItem || '').toUpperCase() !== 'CANCELADO' && Number(it.codprod) > 0)
        .map((it: any) => {
          const q = Number(it.qtd ?? it.qtdneg) || 0;
          return {
            marcado: true,
            codprod: Number(it.codprod),
            pn: String(it.refforn ?? it.referencia ?? it.codprod ?? ''),
            descricao: String(it.descricao ?? ''),
            unidade: String(it.codvol ?? 'UN'),
            sequencia: Number(it.seq) || 0,
            qtdMax: q,
            qtd: q,
          };
        });
      if (this.gvItens.length === 0 && !this.gvErro) this.gvErro = 'Pré-Compra sem itens (não cancelados).';
    } catch (err: any) {
      this.gvErro = err?.message ?? 'Erro ao carregar a Pré-Compra.';
    } finally {
      this.gvCarregando = false;
    }
  }

  /** Define empresa (real/virtual) da Pré-Venda e resolve o CLIENTE = parceiro cujo CNPJ é o da empresa escolhida. */
  async gvAplicarEmpresa(): Promise<void> {
    const op = this.empresa.opcoes.find(o => o.codEmp === this.gvEmpresaSel);
    if (!op) { this.gvErro = 'Empresa não encontrada.'; return; }
    this.gvCodEmp = op.virtual ? Number(op.codEmpReal) : Number(op.codEmp);
    this.gvEmpresaVirtual = op.virtual ? Number(op.codEmp) : 0;
    this.gvCodParc = 0;
    this.gvNomeCliente = '';
    this.gvErro = '';
    // 1) Parceiro cadastrado da empresa (virtuais: GPL_EMPRESA_VIRTUAL.CODPARC; reais: o de mesmo CNPJ)
    if (Number(op.codParcEmpresa) > 0) {
      this.gvCodParc = Number(op.codParcEmpresa);
      try {
        const rc: any = await firstValueFrom(this.gateway.buscarClientePedidoVenda(String(this.gvCodParc)));
        const lista: any[] = (rc?.responseBody ?? rc)?.lista ?? [];
        const c = lista.find((x: any) => String(x.codparc) === String(this.gvCodParc));
        this.gvNomeCliente = c ? `${c.codparc} - ${c.nomeparc}` : String(this.gvCodParc);
      } catch { this.gvNomeCliente = String(this.gvCodParc); }
      return;
    }
    // 2) Sem parceiro vinculado: procura pelo CNPJ da empresa
    const cnpj = String(op.cnpj ?? '').replace(/\D/g, '');
    if (!cnpj) { this.gvErro = `A empresa ${op.codEmp} - ${op.nomeFant || op.razaoAbrev} não tem parceiro (cliente) vinculado nem CNPJ para localizar o cliente.`; return; }
    try {
      const rc: any = await firstValueFrom(this.gateway.buscarClientePedidoVenda(cnpj));
      const lista: any[] = (rc?.responseBody ?? rc)?.lista ?? [];
      const c = lista.find((x: any) => String(x.docparc ?? x.cgcCpf ?? x.cgc_cpf ?? '').replace(/\D/g, '') === cnpj) ?? (lista.length === 1 ? lista[0] : null);
      if (!c) { this.gvErro = `Não há parceiro (cliente) cadastrado com o CNPJ da empresa ${op.codEmp} - ${op.nomeFant || op.razaoAbrev} (${cnpj}).`; return; }
      this.gvCodParc = Number(c.codparc) || 0;
      this.gvNomeCliente = `${c.codparc} - ${c.nomeparc}`;
    } catch (e: any) {
      this.gvErro = e?.message ?? 'Não foi possível localizar o cliente da empresa.';
    }
  }

  async gvTrocarEmpresa(codEmp: string): Promise<void> {
    if (this.gvGerando || this.gvEmpresaSel === codEmp) return;
    this.gvEmpresaSel = codEmp;
    await this.gvAplicarEmpresa();
  }

  /** Cria a Pré-Venda (TOP 1000, ABERTA) com os itens marcados, preço de venda atual e o cliente da Pré-Compra. */
  async confirmarGerarPreVenda(): Promise<void> {
    const p = this.gvPre;
    if (!p || this.gvGerando || this.gvCarregando || this.gvErro) return;
    const alvo = this.gvItens.filter(i => i.marcado && i.qtd > 0);
    if (alvo.length === 0) { this.gvErro = 'Marque ao menos um item com quantidade maior que zero.'; return; }
    this.gvGerando = true;
    this.gvErro = '';
    try {
      // Cotação do dia (a mais recente) e vendedor logado.
      let taxa = 0;
      try {
        const rc: any = await firstValueFrom(this.gateway.buscarCotacoesDemonstrativo());
        const cots = (rc?.responseBody ?? rc)?.cotacoes ?? [];
        taxa = parseFloat(String(cots[0]?.valor ?? '0').replace(',', '.')) || 0;
      } catch { /* sem cotação: segue sem R$ */ }
      if (!(taxa > 0)) { this.gvErro = 'Não foi possível obter a cotação do dólar do dia.'; return; }
      let codVend: number | null = null;
      try {
        const rv: any = await firstValueFrom(this.gateway.buscarVendedorLogadoPedidoVenda());
        const bv = rv?.responseBody ?? rv;
        const v = bv?.vendedor ?? bv ?? {};
        codVend = Number(v?.codVend) > 0 ? Number(v.codVend) : null;
      } catch { /* sem vendedor ligado ao usuário */ }

      const itens: any[] = [];
      for (const it of alvo) {
        let vlrVenda = 0, vlrCore = 0, core = false, codlocal = 0;
        try {
          const rp: any = await firstValueFrom(this.gateway.buscarPrecoExc(it.codprod, this.gvCodEmp));
          const bp = rp?.responseBody ?? rp;
          const info = bp?.info ?? bp;
          vlrVenda = parseFloat(String(info?.vlrVenda ?? '0').replace(',', '.')) || 0;
          vlrCore = parseFloat(String(info?.vlrCore ?? '0').replace(',', '.')) || 0;
          core = String(info?.controlaCore ?? 'N').toUpperCase() === 'S';
        } catch { /* item segue sem preço: o vendedor ajusta na Pré-Venda */ }
        try {
          const rl: any = await firstValueFrom(this.gateway.buscarPecasModal({ REFFORN: it.pn, CODEMP: this.gvCodEmp }));
          const lst: any[] = (rl?.responseBody ?? rl)?.lista ?? [];
          const f = lst.find((x: any) => Number(x.codprod) === it.codprod);
          codlocal = Number(f?.codlocalpadrao) || 0;
        } catch { /* local padrão fica 0 */ }
        const usd = parseFloat((vlrVenda + (core ? vlrCore : 0)).toFixed(2));
        const brl = parseFloat((usd * taxa).toFixed(2));
        itens.push({
          codprod: it.codprod, codvol: it.unidade || 'UN', qtdneg: it.qtd,
          vlrunit: brl, vlrunitmoe: usd, vlrtotmoe: parseFloat((usd * it.qtd).toFixed(2)),
          percdesc: 0, vlrdesc: 0, vlracrescdesc: 0, codlocal,
          vlrtot: parseFloat((brl * it.qtd).toFixed(2)),
          vlripi: 0, vlrsubst: 0,
          ad_core: core ? 'S' : 'N', ad_vlrcore: vlrCore,
        });
      }

      const hoje = new Date();
      const dataBr = `${String(hoje.getDate()).padStart(2, '0')}/${String(hoje.getMonth() + 1).padStart(2, '0')}/${hoje.getFullYear()}`;
      const payload = {
        cabecalho: {
          codEmp: this.gvCodEmp,
          empresaVirtual: this.gvEmpresaVirtual || 0,
          codParc: this.gvCodParc,
          codTipOper: 1000,
          codTipVenda: 1,
          codCenCus: 202000,
          codNat: 1010101,
          observacao: `Gerada da Pré-Compra #${p.nunota}`,
          obsInterna: '',
          numTitulo: '',
          tipoNumTitulo: 'PVE',
          codVend,
          dataEmissao: dataBr,
          dtNegociacao: dataBr,
          tipoGpl: p.tipoGpl || '',
          gplStatus: 'ABERTO',
          codAero: 0,
          vlrMoeda: taxa,
          vlrDescTot: 0,
          percDesc: 0,
          vlrOutros: 0,
        },
        itens,
      };
      const res: any = await firstValueFrom(this.gateway.gravarPedidoVenda(payload));
      const body = res?.responseBody ?? res;
      if (!(body?.sucesso === true || body?.sucesso === 'true') || !Number(body?.nunota)) {
        this.gvErro = body?.erro ?? res?.statusMessage ?? 'Não foi possível gerar a Pré-Venda.';
        return;
      }
      const nunotaPv = Number(body.nunota);
      // Origem: grava o nº da Pré-Compra na Pré-Venda (aparece na coluna "Pré-Compra Origem" da lista).
      let avisoOrigem = '';
      try {
        await firstValueFrom(this.gateway.chamarServiceGpl('PedidoVendaGlobalSP.doAtualizarIdsPedCompraSankhya',
          { nunota: nunotaPv, idsPedCompra: String(p.nunota) }));
      } catch { avisoOrigem = 'A Pré-Venda foi criada, mas não foi possível gravar a Pré-Compra de origem.'; }
      this.gvAberto = false;
      await this.informar(`Pré-Venda #${nunotaPv} gerada a partir da Pré-Compra #${p.nunota}.`,
        avisoOrigem ? 'aviso' : 'sucesso', avisoOrigem ? [avisoOrigem] : [], 'Pré-Venda gerada');
    } catch (err: any) {
      this.gvErro = err?.error?.erro ?? err?.message ?? 'Erro ao gerar a Pré-Venda.';
    } finally {
      this.gvGerando = false;
    }
  }

  // ── Ações > Status (Autorizar / Cancelar / Estornar) ──
  private statusNormalizado(p: PreCompraLinha): string {
    const s = (p.gplStatus || '').trim().toUpperCase();
    return !s || s === 'TESTE' ? 'ABERTO' : s;   // pedidos antigos (TESTE) contam como ABERTO
  }
  /** Edição só com o pedido ABERTO (ou TESTE/vazio); nos demais status a tela abre só para visualização. */
  podeEditar(p: PreCompraLinha): boolean { return this.statusNormalizado(p) === 'ABERTO'; }
  podeAutorizar(p: PreCompraLinha): boolean { return this.statusNormalizado(p) === 'ABERTO'; }
  podeCancelarPedido(p: PreCompraLinha): boolean { return this.statusNormalizado(p) === 'ABERTO'; }
  /** "Cancelar itens em aberto" só vale para pedidos BACKORDERS. */
  podeCancelarItensEmAberto(p: PreCompraLinha): boolean { return this.statusNormalizado(p) === 'BACKORDERS'; }
  podeEstornar(p: PreCompraLinha): boolean { const st = this.statusNormalizado(p); return st === 'AUTORIZADO' || st === 'CANCELADO'; }

  async alterarStatus(p: PreCompraLinha, acao: 'AUTORIZAR' | 'CANCELAR' | 'ESTORNAR'): Promise<void> {
    this.dropdownAberto = null;
    const ok = acao === 'AUTORIZAR' ? this.podeAutorizar(p) : acao === 'CANCELAR' ? this.podeCancelarPedido(p) : this.podeEstornar(p);
    if (!ok) return;
    const textos = {
      AUTORIZAR: `Autorizar o Pré-Compra #${p.nunota}? O cabeçalho ficará AUTORIZADO no Sankhya e no Aerosys.`,
      CANCELAR:  `CANCELAR o Pré-Compra #${p.nunota}? O cabeçalho e todos os itens ficarão CANCELADOS no Sankhya e no Aerosys.`,
      ESTORNAR:  this.statusNormalizado(p) === 'CANCELADO'
        ? `Estornar o Pré-Compra #${p.nunota}? O cabeçalho e os itens cancelados voltarão para ABERTO no Sankhya e no Aerosys.`
        : `Estornar o Pré-Compra #${p.nunota}? Ele voltará para ABERTO no Sankhya e no Aerosys.`
    };
    const titulos = { AUTORIZAR: 'Autorizar pedido', CANCELAR: 'Cancelar pedido', ESTORNAR: 'Estornar pedido' };
    const botoes = { AUTORIZAR: 'Autorizar', CANCELAR: 'Cancelar pedido', ESTORNAR: 'Estornar' };
    if (!(await this.confirmar(textos[acao], { titulo: titulos[acao], textoConfirmar: botoes[acao], perigo: acao !== 'AUTORIZAR' }))) return;
    try {
      const res: any = await firstValueFrom(this.gateway.alterarStatusPreCompra(p.nunota, acao));
      if (res?.sucesso) {
        const avisos: string[] = [];
        if (res.aerosysStatusOk === false) avisos.push('não foi possível atualizar o status no Aerosys: ' + (res.aerosysStatusErro ?? ''));
        if (res.aerosysItensOk === false) avisos.push('não foi possível atualizar os itens no Aerosys');
        if (acao === 'AUTORIZAR') avisos.push(...await this.atualizarPrecoFabricanteDosItens(p));
        this.buscar();
        // Se os itens desta pré-compra estão abertos, recarrega (cancelar/estornar muda o status dos itens).
        if (this.itensAberto && this.itensNunota === p.nunota) {
          void this.abrirItens(new Event('refresh'), p, true);
        }
        await this.informar(`Pré-Compra #${p.nunota} agora está ${res.statusNovo}.`, avisos.length ? 'aviso' : 'sucesso',
          avisos.map(a => a.charAt(0).toUpperCase() + a.slice(1) + (a.endsWith('.') ? '' : '.')));
      } else {
        await this.informar(res?.erro ?? 'Erro desconhecido.', 'erro', [], 'Não foi possível alterar o status');
      }
    } catch (e: any) {
      await this.informar(e?.error?.erro ?? e?.message ?? 'Erro desconhecido.', 'erro', [], 'Não foi possível alterar o status');
    }
  }


  // ── Itens da pré-compra (clique no Código) ─────────────────────
  // Mesmos campos do lançamento de itens da Pré-Compra de Compra (pedido-compra-form).
  itensAberto = false;
  itensCarregando = false;
  itensErro = '';
  itensNunota: number | null = null;
  itens: any[] = [];
  itemSelecionado: number | null = null;

  /** Itens marcados para as ações em lote (cancelar...): clique na linha ou na caixa alterna a marcação. */
  itensMarcados = new Set<number>();

  selecionarItem(i: number, event: Event): void {
    event.stopPropagation();
    if (this.itensMarcados.has(i)) this.itensMarcados.delete(i); else this.itensMarcados.add(i);
    // itemSelecionado = último marcado (usado pelas ações de um item só, como a previsão de entrega)
    this.itemSelecionado = this.itensMarcados.size ? Array.from(this.itensMarcados).pop()! : null;
  }

  get todosItensMarcados(): boolean {
    return this.itens.length > 0 && this.itensMarcados.size === this.itens.length;
  }

  toggleTodosItens(event: Event): void {
    event.stopPropagation();
    if (this.todosItensMarcados) {
      this.itensMarcados.clear();
      this.itemSelecionado = null;
    } else {
      this.itens.forEach((_, i) => this.itensMarcados.add(i));
      this.itemSelecionado = this.itens.length ? this.itens.length - 1 : null;
    }
  }

  fmtDec(v: number | null | undefined): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  }

  private dataAnalitico(deslocMeses: number): string {
    const d = new Date();
    d.setMonth(d.getMonth() + deslocMeses);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  /** recarregar=true: atualiza os itens já abertos sem fechar/limpar o painel (evita o "pulo" da tela). */
  async abrirItens(event: Event, p: PreCompraLinha, recarregar = false): Promise<void> {
    event.stopPropagation();
    this.dropdownAberto = null;
    if (!recarregar && this.itensAberto && this.itensNunota === p.nunota) { this.fecharItens(); return; }
    let tipoGplDetalhe = '';
    const itensAnteriores = recarregar ? this.itens : [];
    this.itensNunota = p.nunota;
    this.itensAberto = true;
    if (!recarregar) {
      this.itensCarregando = true;
      this.itensErro = '';
      this.itens = [];
      this.itemSelecionado = null;
      this.itensMarcados.clear();
    }
    try {
      const res: any = await firstValueFrom(this.gateway.buscarDetalhesPedidoVenda(p.nunota));
      const body = res?.responseBody ?? res;
      const d = body?.sucesso ? (body.detalhe ?? body) : null;
      const brutos: any[] = d?.itens ?? [];
      tipoGplDetalhe = String(d?.tipoGpl ?? '').trim();
      if (!d || brutos.length === 0) {
        this.itensErro = d ? '' : 'Pré-Compra não encontrada.';
        return;
      }
      const novosItens = brutos.map((it: any) => {
        const st = String(it.statusItem || '').toUpperCase();
        const qtd = Number(it.qtd ?? it.qtdneg) || 0;
        // Custo = Preço do Fabricante, igual ao form: U$ (vlrunitmoe) e R$ (vlrunit) gravados no pedido.
        const custoUsd = Number(it.precoUsd) || Number(it.vlrunitmoe) || 0;
        const custoBrl = Number(it.vlrunit) || 0;
        return {
          codprodInterno: Number(it.codprod) || 0,
          pn: it.refforn ?? it.referencia ?? String(it.codprod ?? ''),
          descricao: it.descricao ?? '',
          unidade: it.codvol ?? 'UN',
          quantidade: qtd,
          fabricante: '',
          custoUsd,
          custoBrl,
          totalUsd: Number(it.vlrtotmoe) || parseFloat((custoUsd * qtd).toFixed(2)),
          totalBrl: Number(it.vlrtot) || parseFloat((custoBrl * qtd).toFixed(2)),
          obs: String(it.obsItem ?? ''),
          statusItem: ['ABERTO', 'CANCELADO', 'COMPRADO', 'BACKORDERS'].includes(st) ? st : 'ABERTO',
          sequencia: Number(it.seq) || 0,
          previsaoEntrega: String(it.previsaoEntrega ?? ''),
          estoque: null, cd: '', custo: null, qtdeVendas: null, qtdeVendasAnoAnt: null,
          pontoPedido: null, qtdePedidoOutros: null, pedidoOutros: '',
          qtdePrecompraAbBk: null, dataPrecompraAbBk: '', estoqueEua: null,
        };
      });
      if (recarregar) {
        // Mantém as colunas de apoio já carregadas (estoque etc.) até o refresh em segundo plano: sem "piscar".
        for (const n of novosItens as any[]) {
          const ant: any = itensAnteriores.find((a: any) => a.sequencia === n.sequencia && a.codprodInterno === n.codprodInterno);
          if (ant) {
            for (const c of ['fabricante', 'estoque', 'cd', 'custo', 'qtdeVendas', 'qtdeVendasAnoAnt', 'pontoPedido',
              'qtdePedidoOutros', 'pedidoOutros', 'qtdePrecompraAbBk', 'dataPrecompraAbBk', 'estoqueEua']) {
              n[c] = ant[c];
            }
          }
        }
      }
      this.itens = novosItens;
    } catch (err: any) {
      this.itensErro = err?.message ?? 'Erro ao buscar os itens da pré-compra.';
      return;
    } finally {
      this.itensCarregando = false;
    }

    // Colunas de apoio (estoque + Aerosys) carregam em segundo plano, como no lançamento.
    const nunota = p.nunota;
    const itensAtuais = this.itens;
    const codemp = Number(p.codemp) || Number(this.empresa.selecionada) || 0;
    const tipo = (tipoGplDetalhe || p.tipoGpl || '').toUpperCase().trim();
    const empresaAerosys = tipo === 'NACIONAL' ? 1 : 12;
    const codProds = Array.from(new Set(itensAtuais.map(i => i.codprodInterno).filter((c: number) => c > 0)));

    itensAtuais.forEach(async (item: any) => {
      if (!item.codprodInterno) return;
      try {
        const r: any = await firstValueFrom(this.gateway.buscarEstoquePorLocal(item.codprodInterno));
        const lista: any[] = (r?.responseBody ?? r)?.locais ?? [];
        let total = 0;
        for (const loc of lista) {
          const emp = parseInt(String(loc.codemp ?? '0'), 10);
          if (codemp > 0 && emp !== codemp) continue;
          total += parseFloat(String(loc.disponivel ?? '0').replace(',', '.')) || 0;
        }
        item.estoque = parseFloat(total.toFixed(3));
      } catch { /* ignora */ }
    });

    if (codProds.length > 0 && codemp > 0) {
      const aplicar = async (empAero: number): Promise<boolean> => {
        const r: any = await firstValueFrom(this.gateway.buscarAnaliticoItensPedidoCompra(
          codProds, codemp, empAero, this.dataAnalitico(-3), this.dataAnalitico(0)));
        const linhas: any[] = Array.isArray(r) ? r : (r?.itens ?? []);
        const porCod = new Map<number, any>();
        for (const l of linhas) if (l?.codProd != null) porCod.set(Number(l.codProd), l);
        if (this.itensNunota !== nunota) return true;
        let achouAerosys = false;
        for (const item of itensAtuais) {
          const dd = porCod.get(item.codprodInterno);
          if (!dd) continue;
          if (dd.cd != null || dd.custo != null || dd.estoqueEua != null) achouAerosys = true;
          item.cd = dd.cd ?? '';
          item.fabricante = dd.fabricante ?? '';
          item.custo = dd.custo ?? null;
          item.pontoPedido = dd.pontoPedido ?? null;
          item.estoqueEua = dd.estoqueEua ?? null;
          item.qtdeVendas = dd.qtdeVendas ?? null;
          item.qtdeVendasAnoAnt = dd.qtdeVendasAnoAnt ?? null;
          item.qtdePedidoOutros = dd.qtdePedidoOutros ?? null;
          item.pedidoOutros = dd.pedidoOutros ?? '';
          item.qtdePrecompraAbBk = dd.qtdePrecompraAbBk ?? null;
          item.dataPrecompraAbBk = dd.dataPrecompraAbBk ?? '';
        }
        return achouAerosys;
      };
      try {
        // Se o Aerosys nao devolveu nada pra empresa deduzida pelo tipo, tenta a outra.
        if (!(await aplicar(empresaAerosys))) await aplicar(empresaAerosys === 1 ? 12 : 1);
      } catch { /* painel de apoio: falha nao trava a lista */ }
    }
  }

  fecharItens(): void { this.itensAberto = false; this.itensFuncoesAberto = false; }


  itensFuncoesAberto = false;



  // ── Utilitários de data ──────────────────────────────────

  private toInputDate(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  private toSankhyaDate(iso: string): string {
    const [y, m, dia] = iso.split('-');
    return `${dia}/${m}/${y}`;
  }
}
